import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import store from 'store';
import { Text } from '@perch-labs/shrike';
import { ErrorPage } from 'components/errors';
import { LoadingPage } from 'components/loading';
import { copyFor, routes } from 'config';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { UTILITY_CREDENTIAL_ID_FOR_SYNC } from 'domain/utility-credentials';
import { Page } from 'styles';
import { BackLink } from 'components/back-link';
import { DifferentUsernameAccordion } from 'pages/settings/components/different-username-accordion';
import { ApiToken } from './forms/api-token.component';
import { GET_UTILITY_SYNC_EDIT_DATA } from './utility-sync-edit.api';

const getCopy = copyFor('settings.utilitySyncEdit');
const { settings, utilitySync } = routes();

export const UtilitySyncEdit = () => {
  const history = useHistory();

  // need to grab this id from the store because Ameren does not support routes with ids
  const utilityCredentialId = store.get(UTILITY_CREDENTIAL_ID_FOR_SYNC);
  const { data, loading, error } = useQuery<
    GetUtilitySyncEditDataQuery,
    GetUtilitySyncEditDataQueryVariables
  >(GET_UTILITY_SYNC_EDIT_DATA, {
    variables: { utilityCredentialId },
  });

  useEffect(() => {
    if (!utilityCredentialId) history.push(utilitySync);
  }, [history, utilityCredentialId]);

  if (loading) return <LoadingPage />;
  if (error || !data) {
    return <ErrorPage title={getCopy('error.title')} to={settings} />;
  }

  const { utilityCredential } = data;
  const { utility } = utilityCredential;
  const utilityName = utility.displayName;

  return (
    <>
      <BackLink
        to={routes({ utilityCredentialId }).utilitySyncCredential}
        location={getCopy('backLink.location')}
      />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h2" margin={{ bottom: '48px' }}>
          {getCopy('title', { utilityName })}
        </Text>
        <Text margin={{ bottom: '16px' }}>{getCopy('description')}</Text>
        <ApiToken
          utilityCredentialId={utilityCredential.id}
          utilityId={utility.id}
          isSynced={utilityCredential.verificationStatus === 'correct'}
        />
      </Page>
      {utility.overrideEmailIsUsername && <DifferentUsernameAccordion />}
    </>
  );
};
